import { AxiosError } from "axios";
import { api } from "./config/api";
import type { PackageDefinition, CreditPurchase } from "../constants/packages";
import { extractError, type ApiErrorBody } from "./config/extractError";

export type PendingPackagePurchase = {
  id: number;
  package_definition_id: number;
  credits_total: number;
  credits_remaining: number;
  duration_hours: number;
  status: "pending";
  created_at: string;
  name: string;
  session_count: number;
  price_cents: number;
  expiry_days: number;
  expires_in_seconds: number;
  // Referral hours already locked into this pending checkout. Resuming charges
  // final_amount_cents, not price_cents — the cart must quote the same figure.
  referral_hours_applied?: number;
  referral_discount_cents?: number;
  final_amount_cents?: number;
};

export async function getPackageDefinitions(): Promise<{
  data: PackageDefinition[] | null;
  error: string | null;
}> {
  try {
    const res = await api.get<{ definitions: PackageDefinition[] }>("/packages/definitions");
    return { data: res.data.definitions, error: null };
  } catch (err) {
    return { data: null, error: extractError(err) };
  }
}

export async function getPackageDefinitionById(id: number): Promise<{
  data: PackageDefinition | null;
  error: string | null;
}> {
  try {
    const res = await api.get<{ definition: PackageDefinition }>(`/packages/definitions/${id}`);
    return { data: res.data.definition, error: null };
  } catch (err) {
    return { data: null, error: extractError(err) };
  }
}

export async function getMyPackages(): Promise<{
  data: CreditPurchase[] | null;
  error: string | null;
}> {
  try {
    const res = await api.get<{ packages: CreditPurchase[] }>("/packages/mine");
    return { data: res.data.packages, error: null };
  } catch (err) {
    return { data: null, error: extractError(err) };
  }
}

export async function getActivePackages(): Promise<{
  data: CreditPurchase[] | null;
  error: string | null;
}> {
  try {
    const res = await api.get<{ packages: CreditPurchase[] }>("/packages/active");
    return { data: res.data.packages, error: null };
  } catch (err) {
    return { data: null, error: extractError(err) };
  }
}

export async function getPendingPackagePurchases(): Promise<{
  data: PendingPackagePurchase[] | null;
  error: string | null;
}> {
  try {
    const res = await api.get<{ packages: PendingPackagePurchase[] }>("/packages/pending");
    return { data: res.data.packages, error: null };
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 401) {
      return { data: null, error: null };
    }
    return { data: null, error: extractError(err) };
  }
}

export async function createPackageCheckout(payload: {
  definition_id: number;
  cancel_url: string;
  auto_checkout?: boolean;
  // Referral reward hours to redeem, at a flat $25/hr. Opt-in: omitting it redeems
  // nothing, so a caller unaware of the feature can never spend a buyer's vouchers.
  referral_hours_to_apply?: number;
}): Promise<{
  data: { url: string | null; purchase_id: number; flow: "hosted_checkout" | "auto_checkout_pending" } | null;
  error: string | null;
}> {
  try {
    const res = await api.post<{ url: string | null; purchase_id: number; flow: "hosted_checkout" | "auto_checkout_pending" }>(
      "/packages/checkout",
      payload,
    );
    return { data: res.data, error: null };
  } catch (err) {
    return { data: null, error: extractError(err) };
  }
}

export async function resumePackageCheckout(payload: {
  purchase_id: number;
  cancel_url: string;
}): Promise<{
  data: { url: string | null; purchase_id: number; flow: "hosted_checkout" } | null;
  error: string | null;
  errorCode?: string | null;
}> {
  try {
    const res = await api.post<{ url: string | null; purchase_id: number; flow: "hosted_checkout" }>(
      `/packages/${payload.purchase_id}/resume-checkout`,
      { cancel_url: payload.cancel_url },
    );
    return { data: res.data, error: null, errorCode: null };
  } catch (err) {
    const body = err instanceof AxiosError ? err.response?.data as ApiErrorBody | undefined : undefined;
    return {
      data: null,
      error: body?.error ?? extractError(err),
      errorCode: body?.code ?? null,
    };
  }
}

export async function discardPendingPackagePurchase(purchaseId: number): Promise<{
  error: string | null;
}> {
  try {
    await api.delete(`/packages/${purchaseId}/draft`);
    return { error: null };
  } catch (err) {
    return { error: extractError(err) };
  }
}

export async function getPackagePurchaseStatus(purchaseId: number): Promise<{
  data: {
    id: number;
    package_definition_id?: number;
    credits_total?: number;
    credits_remaining?: number;
    duration_hours?: number;
    status: "pending" | "active" | "depleted" | "expired" | "lapsed";
    purchased_at: string | null;
    expires_at: string | null;
    amount_paid_cents: number | null;
    whatsapp_confirmation_status?: "sent" | "delivered" | "read" | "failed" | "undelivered" | null;
    name?: string;
    session_count?: number;
    price_cents?: number;
    expiry_days?: number;
  } | null;
  error: string | null;
}> {
  try {
    const res = await api.get(`/packages/${purchaseId}/status`);
    return { data: res.data, error: null };
  } catch (err) {
    return { data: null, error: extractError(err) };
  }
}

export async function downloadPackageReceipt(purchaseId: number): Promise<{
  error: string | null;
}> {
  try {
    const res = await api.get(`/packages/${purchaseId}/receipt`, { responseType: "arraybuffer" });
    const blob = new Blob([res.data as ArrayBuffer], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `package-receipt-${purchaseId}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    return { error: null };
  } catch (err) {
    return { error: extractError(err) };
  }
}

export async function resendFailedPackageWhatsAppMessages() {
  try {
    const response = await api.post("/packages/whatsapp/resend-failed");
    const data = response.data as {
      success?: boolean;
      resent?: number;
      error?: string;
    };
    const success = Boolean(data.success);
    return {
      success,
      resent: data.resent ?? 0,
      error: success ? null : data.error ?? "Failed to resend package WhatsApp messages.",
    };
  } catch {
    return { success: false, resent: 0, error: "Failed to resend package WhatsApp messages." };
  }
}

export async function resolvePackageBookToken(token: string): Promise<{
  data: { purchase_id: number } | null;
  error: string | null;
}> {
  try {
    const res = await api.get<{ purchase_id: number }>(`/packages/book-link/${encodeURIComponent(token)}`);
    return { data: res.data, error: null };
  } catch (err) {
    return { data: null, error: extractError(err) };
  }
}

export async function bookSessionWithPackage(payload: {
  credit_purchase_id: number;
  cleaner_id: number;
  start_date: string;
  start_time: string;
  end_time: string;
  address: string;
  postal: string;
  floor_unit: string;
  notes?: string;
}): Promise<{ data: { bookingId: number } | null; error: string | null }> {
  try {
    const res = await api.post<{ bookingId: number }>("/packages/book", payload);
    return { data: res.data, error: null };
  } catch (err) {
    return { data: null, error: extractError(err) };
  }
}

// All-or-nothing: books every session in one request, one transaction. Either every
// session is created or none are — no partial credit deduction / partial booking can
// happen (KAN-550 Part 2), unlike calling bookSessionWithPackage once per session.
export async function bookSessionsWithPackageBatch(payload: {
  credit_purchase_id: number;
  sessions: {
    cleaner_id: number;
    start_date: string;
    start_time: string;
    end_time: string;
    address: string;
    postal: string;
    floor_unit: string;
    notes?: string;
  }[];
}): Promise<{
  data: { bookingIds: number[] } | null;
  error: string | null;
  // Which session's start_date (yyyy-MM-dd) caused the failure, if the backend could
  // tell — lets the caller reset just that one session instead of the whole batch.
  conflictDate?: string | null;
  // Machine-readable cause. The backend tags several unrelated failures with a
  // conflictDate (BOOKING_CONFLICT, SLOT_CONFLICT, SESSION_AFTER_EXPIRY,
  // CLEANER_NOT_FOUND, CREDIT_DEDUCT_FAILED), so callers must branch on this rather
  // than assume "conflictDate present" means the time slot itself was taken.
  errorCode?: string | null;
}> {
  try {
    const res = await api.post<{ bookingIds: number[] }>("/packages/book-batch", payload);
    return { data: res.data, error: null, conflictDate: null, errorCode: null };
  } catch (err) {
    const body = err instanceof AxiosError ? (err.response?.data as (ApiErrorBody & { conflictDate?: string | null }) | undefined) : undefined;
    return {
      data: null,
      error: body?.error ?? extractError(err),
      conflictDate: body?.conflictDate ?? null,
      errorCode: body?.code ?? null,
    };
  }
}
